import { type AnimationState, columnFractions, type TableElement } from "@cva/shared";
import type { CSSProperties, ReactNode } from "react";
import { boxStyle, fontFamilyCss } from "./styles";

/**
 * Table element: an HTML table in the box width (text stays vector text in
 * the print PDF). The height follows the content; the editor keeps the box
 * height in sync by measuring `[data-text-content]`.
 */
export function renderTable(
  element: TableElement,
  state: AnimationState,
  extraFilter?: string,
): ReactNode {
  const box = boxStyle(element, state);
  const fractions = columnFractions(element);
  const border =
    element.borderWidth > 0 ? `${element.borderWidth}px solid ${element.borderColor}` : "none";
  const columns = element.columns.length;
  const cellStyle = (row: number, column: number): CSSProperties => {
    const header = element.header && row === 0;
    const bodyIndex = element.header ? row - 1 : row;
    const fill = header
      ? element.headerFill
      : element.zebra && bodyIndex % 2 === 1
        ? element.stripeFill
        : element.bodyFill;
    return {
      border,
      padding: element.padding,
      height: element.rowHeight > 0 ? element.rowHeight : undefined,
      background: fill,
      color: header ? element.headerColor : element.color,
      fontWeight: header ? Math.max(700, element.fontWeight) : element.fontWeight,
      textAlign: element.columns[column]?.align ?? "left",
      verticalAlign: "middle",
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
      boxSizing: "border-box",
    };
  };
  return (
    <div
      key={element.id}
      data-element-id={element.id}
      style={{ ...box, filter: extraFilter ?? box.filter }}
    >
      <table
        data-text-content=""
        style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed",
          fontFamily: fontFamilyCss(element.fontFamily),
          fontSize: element.fontSize,
          lineHeight: element.lineHeight,
          margin: 0,
        }}
      >
        <colgroup>
          {fractions.map((fraction, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: columns have no identity
            <col key={index} style={{ width: `${fraction * 100}%` }} />
          ))}
        </colgroup>
        <tbody>
          {element.cells.map((row, rowIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity
            <tr key={rowIndex}>
              {Array.from({ length: columns }, (_, column) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: cells have no identity
                <td key={column} style={cellStyle(rowIndex, column)}>
                  {row[column] || " "}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
